import { useDispatch } from 'react-redux';

import { CoinMeta, KlineDataPoint, reduxIcons, reduxIsLoading, reduxKlineData, reduxLastFetch, setCoinIcons, setIsLoading } from "../stores/coinIconsSlice";
import { useAppSelector } from "./storeHook";
import { getHyperBackendUrl } from '../utils/client.utils';
import { useCallback } from 'react';

const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

export function useCoinIcons() {
  const dispatch = useDispatch();

  const icons = useAppSelector(reduxIcons);
  const klineData = useAppSelector(reduxKlineData);
  const lastFetched = useAppSelector(reduxLastFetch);
  const isLoading = useAppSelector(reduxIsLoading);

  const fetchIcons = useCallback(async (forceRefresh = false) => {
    // Check if data is still fresh (unless force refresh)
    if (
      !forceRefresh
      && lastFetched
      && Date.now() - lastFetched < CACHE_DURATION
    ) {
      return
    }

    // Prevent multiple simultaneous fetches
    if (isLoading) {
      return
    }

    dispatch(setIsLoading(true))

    try {
      const url = `${getHyperBackendUrl()}/api/market/market/tokens/meta`

      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data: CoinMeta[] = await response.json()

      // Convert array to objects for faster lookups
      const iconsMap: Record<string, string> = {}
      const klineMap: Record<string, KlineDataPoint[]> = {}

      data.forEach((coin) => {
        iconsMap[coin.symbol] = coin.icon
        if (coin.kline_24h) {
          klineMap[coin.symbol] = coin.kline_24h
        }
      })

      dispatch(setCoinIcons({
        icons: iconsMap,
        klineData: klineMap,
        lastFetched: Date.now(),
        isLoading: false,
      }))
    }
    catch (error) {
      console.error('Failed to fetch coin icons:', error)
      dispatch(setIsLoading(false))
      // Keep existing data if fetch fails
    }
  }, [icons, klineData, lastFetched, isLoading])

  const getIconUrl = useCallback((symbol: string) => {
    return icons[symbol] || null
  }, [icons])

  const getKlineData = useCallback((symbol: string) => {
    return klineData[symbol] || null
  }, [klineData])

  const clearIcons = useCallback(() => {
    dispatch(setCoinIcons({
      icons: {},
      klineData: {},
      lastFetched: null,
      isLoading: false,
    }))
  }, [])

  return {
    icons,
    klineData,
    lastFetched,
    isLoading,
    fetchIcons,
    getIconUrl,
    getKlineData,
    clearIcons
  }
}